import {
  fitAssessmentResultSchema,
  fitAssessmentSuccessResponseSchema,
  type FitAssessmentResult,
  type FitAssessmentSuccessResponse,
} from '@shared/fit-analysis'

type CanvasSourceKind = 'wrapped-response' | 'bare-result'

export type FitAssessmentCanvasParseResult =
  | {
      success: true
      result: FitAssessmentResult
      sourceKind: CanvasSourceKind
    }
  | {
      success: false
      error: string
    }

const getIssueMessage = (issues: Array<{ path: PropertyKey[]; message: string }>): string => {
  const firstIssue = issues[0]
  if (!firstIssue) {
    return 'Payload does not match the fit assessment schema.'
  }

  const path = firstIssue.path.length > 0 ? firstIssue.path.join('.') : 'payload'

  return `${path}: ${firstIssue.message}`
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

export const serializeFitAssessmentCanvasPayload = (
  payload: FitAssessmentSuccessResponse | FitAssessmentResult
): string => JSON.stringify(payload, null, 2)

export const parseFitAssessmentCanvasInput = (input: string): FitAssessmentCanvasParseResult => {
  try {
    const payload = JSON.parse(input) as unknown

    const wrappedResponse = fitAssessmentSuccessResponseSchema.safeParse(payload)
    if (wrappedResponse.success) {
      return {
        success: true,
        result: wrappedResponse.data.data,
        sourceKind: 'wrapped-response',
      }
    }

    const bareResult = fitAssessmentResultSchema.safeParse(payload)
    if (bareResult.success) {
      return {
        success: true,
        result: bareResult.data,
        sourceKind: 'bare-result',
      }
    }

    return {
      success: false,
      error:
        isRecord(payload) && 'success' in payload
          ? getIssueMessage(wrappedResponse.error.issues)
          : getIssueMessage(bareResult.error.issues),
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown JSON parsing error'
    return {
      success: false,
      error: `Invalid JSON: ${message}`,
    }
  }
}
